// Classification for rejected join_tournament_response payloads
// (success: false). The server sends two distinct messages that both happen
// to contain "already", so a naive substring check conflates them:
//   - "Already registered in an overlapping tournament (X)." — a genuine
//     rejection; the user must see the failure modal.
//   - "Player is already registered in this tournament." — a harmless
//     duplicate join; the existing PlayChess handler deliberately discards
//     it (joinTnrSuccess === 0, no modal) rather than treating it as an
//     error.
//
// This is imported by BOTH PlayChess's direct joinTournamentResponseCallback
// AND SocketProvider's GamePlayStore.joinFailure bridge effect, so the two
// can never classify the same message differently. That matters because
// cc-client-sdk invokes every registered handler for a message type
// (lib/index.js) — both paths fire for the same event whenever PlayChess is
// mounted, so they must agree (double-firing must be idempotent).
export type JoinFailureKind = 'overlapping' | 'already' | 'other';

export function classifyJoinFailureMessage(
  message?: string | null,
): JoinFailureKind {
  const lower = message?.toLowerCase?.() ?? '';
  if (lower.includes('overlapping')) return 'overlapping';
  if (lower.includes('already')) return 'already';
  return 'other';
}

// Maps a rejected join to the joinTnrSuccess value TournamentPlay expects:
//   0 = neutral (harmless duplicate join — no modal, no message)
//   1 = show the join-failure modal with the server's message
export function resolveJoinTnrSuccess(message?: string | null): 0 | 1 {
  return classifyJoinFailureMessage(message) === 'already' ? 0 : 1;
}

export type JoinConflict = {id: number; name?: string};

// Only trust STRUCTURED conflict data. A message-text match is not enough --
// without an id there is nothing to withdraw from, and matching on name is
// unsafe because tournament names repeat weekly. The backend rollout may not
// be deployed yet, so this must return null (not throw) on any payload shape
// that predates conflict_id/conflict_name.
export function extractJoinConflict(payload?: {
  error_code?: string;
  conflict_id?: number | string;
  conflict_name?: string;
} | null): JoinConflict | null {
  if (!payload) return null;
  const id = Number(payload.conflict_id);
  if (!Number.isFinite(id) || id <= 0) return null;
  return {
    id,
    ...(payload.conflict_name ? {name: payload.conflict_name} : {}),
  };
}
